import * as React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  TextField,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { UserSubscribedModel } from "@/app/models/checkout/types";
import CommonHelper from "@/app/helpers/CommonHelper";

export type DeliveryNotificationsValues = {
  contactByEmailCC: boolean;
  contactByTextCC: boolean;
  emails: string[];
  phones: string[];
};

type Props = {
  userSubscribedModel: UserSubscribedModel[];
  subscribedExtraEmails: string[];
  subscribedExtraPhones: string[];
  initialContactByEmailCC?: boolean;
  initialContactByTextCC?: boolean;

  strings?: Partial<{
    enrolledUsersTitle: string;
    additionalUsersTitle: string;
    emailCheckbox: string;
    textCheckbox: string;
    addNewEmail: string;
    addNewPhone: string;
    submit: string;
    invalidEmail: string;
    invalidPhone: string;
    emailRequired: string;
    phoneRequired: string;
  }>;

  onSubmit?: (values: DeliveryNotificationsValues) => void;
  onContinueShopping?: () => void;
};

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const isValidPhone = (phone: string) =>
  /^\d{3}-\d{3}-\d{4}$/.test(phone.trim());

const DeliveryNotifications: React.FC<Props> = ({
  userSubscribedModel,
  subscribedExtraEmails,
  subscribedExtraPhones,
  initialContactByEmailCC = false,
  initialContactByTextCC = false,
  strings,
  onSubmit,
  onContinueShopping,
}) => {
  const t = {
    enrolledUsersTitle:
      "The following users will receive delivery notifications on this order",
    additionalUsersTitle:
      "Would you like additional users to receive delivery notifications on this order?",
    emailCheckbox: "Email",
    textCheckbox: "Text",
    addNewEmail: "Add New Email",
    addNewPhone: "Add New Phone",
    submit: "SUBMIT",
    invalidEmail: "Please enter a valid email address.",
    invalidPhone: "Please enter a valid phone number (XXX-XXX-XXXX).",
    emailRequired: "At least one email is required.",
    phoneRequired: "At least one phone number is required.",
    ...strings,
  };

  const [showAllEnrolled, setShowAllEnrolled] = React.useState(false);
  const enrolledEmails = React.useMemo(() => {
    const seen = new Set<string>();
    const list = userSubscribedModel
      .filter((u) => u.isSubscribedToEmailNotifications)
      .map((u) => (u.userName || "").trim())
      .filter(Boolean);

    const combined = [...list, ...(subscribedExtraEmails || [])];

    const unique = combined.filter((email) => {
      const key = email.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return unique;
  }, [userSubscribedModel, subscribedExtraEmails]);

  const MAX_VISIBLE_ENROLLED = 5;
  const visibleEnrolled = showAllEnrolled
    ? enrolledEmails
    : enrolledEmails.slice(0, MAX_VISIBLE_ENROLLED);
  const hiddenCount = Math.max(
    enrolledEmails.length - visibleEnrolled.length,
    0
  );

  const [contactByEmailCC, setContactByEmailCC] = React.useState(
    initialContactByEmailCC
  );
  const [contactByTextCC, setContactByTextCC] = React.useState(
    initialContactByTextCC
  );

  const [emails, setEmails] = React.useState<string[]>(
    subscribedExtraEmails && subscribedExtraEmails.length > 0
      ? [...subscribedExtraEmails]
      : [""]
  );
  const [emailErrors, setEmailErrors] = React.useState<boolean[]>(
    subscribedExtraEmails && subscribedExtraEmails.length > 0
      ? subscribedExtraEmails.map(() => false)
      : [false]
  );

  React.useEffect(() => {
    if (subscribedExtraEmails && subscribedExtraEmails.length > 0) {
      setEmails([...subscribedExtraEmails]);
      setEmailErrors(subscribedExtraEmails.map(() => false));
    }
  }, [subscribedExtraEmails]);

  const [phones, setPhones] = React.useState<string[]>(
    subscribedExtraPhones && subscribedExtraPhones.length > 0
      ? [...subscribedExtraPhones]
      : [""]
  );

  const [phoneErrors, setPhoneErrors] = React.useState<boolean[]>(
    subscribedExtraPhones && subscribedExtraPhones.length > 0
      ? subscribedExtraPhones.map(() => false)
      : [false]
  );

  React.useEffect(() => {
    if (subscribedExtraPhones && subscribedExtraPhones.length > 0) {
      setPhones([...subscribedExtraPhones]);
      setPhoneErrors(subscribedExtraPhones.map(() => false));
    }
  }, [subscribedExtraPhones]);

  const [formError, setFormError] = React.useState<string>("");

  const addEmail = () => {
    setEmails((a) => [...a, ""]);
    setEmailErrors((a) => [...a, false]);
  };
  const removeEmailAt = (idx: number) => {
    setEmails((a) => (a.length > 1 ? a.filter((_, i) => i !== idx) : a));
    setEmailErrors((a) => (a.length > 1 ? a.filter((_, i) => i !== idx) : a));
  };
  const setEmailAt = (idx: number, val: string) => {
    setEmails((a) => a.map((v, i) => (i === idx ? val : v)));
    setEmailErrors((errs) =>
      errs.map((v, i) =>
        i === idx ? contactByEmailCC && !!val && !isValidEmail(val) : v
      )
    );
  };

  const addPhone = () => {
    setPhones((a) => [...a, ""]);
    setPhoneErrors((a) => [...a, false]);
  };
  const removePhoneAt = (idx: number) => {
    setPhones((a) => (a.length > 1 ? a.filter((_, i) => i !== idx) : a));
    setPhoneErrors((a) => (a.length > 1 ? a.filter((_, i) => i !== idx) : a));
  };
  const setPhoneAt = (idx: number, val: string) => {
    const formatted = CommonHelper.formatPhone334(val);
    setPhones((a) => a.map((v, i) => (i === idx ? formatted : v)));
    setPhoneErrors((errs) =>
      errs.map((v, i) =>
        i === idx
          ? contactByTextCC && !!formatted && !isValidPhone(formatted)
          : v
      )
    );
  };

  const handleSubmit = () => {
    setFormError("");

    if (contactByEmailCC) {
      const trimmed = emails.map((e) => e.trim());
      const nonEmpty = trimmed.filter(Boolean);
      if (nonEmpty.length < 1) {
        setFormError(t.emailRequired);
        return;
      }
      if (nonEmpty.some((e) => !isValidEmail(e))) {
        setFormError(t.invalidEmail);
        return;
      }
    }

    if (contactByTextCC) {
      const trimmed = phones.map((p) => p.trim());
      const nonEmpty = trimmed.filter(Boolean);
      if (nonEmpty.length < 1) {
        setFormError(t.phoneRequired);
        return;
      }
      if (nonEmpty.some((p) => !isValidPhone(p))) {
        setFormError(t.invalidPhone);
        return;
      }
    }

    onSubmit?.({
      contactByEmailCC,
      contactByTextCC,
      emails: emails.map((e) => e.trim()).filter(Boolean),
      phones: phones.map((p) => p.trim()).filter(Boolean),
    });
  };

  const emailRemoveDisabled = emails.length <= 1;
  const phoneRemoveDisabled = phones.length <= 1;

  return (
    <>
      <Card
        elevation={0}
        sx={{ border: "1px solid #ccc", borderRadius: 2, p: { xs: 2, md: 3 } }}
      >
        <CardContent sx={{ p: 0 }}>
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Stack
                alignItems="center"
                justifyContent="flex-start"
                spacing={2}
              >
                <NotificationsNoneOutlinedIcon
                  sx={{ fontSize: 46, color: "#e8c509" }}
                />
                <Typography
                  variant="h6"
                  align="center"
                  sx={{ fontWeight: 600, fontSize: "1.25rem", maxWidth: 460 }}
                >
                  {t.enrolledUsersTitle}
                </Typography>
                <Stack spacing={1} sx={{ width: "100%", maxWidth: 520 }}>
                  {visibleEnrolled.map((email, idx) => (
                    <Typography
                      key={`enrolled-${idx}`}
                      color="text.secondary"
                      align="center"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {email}
                    </Typography>
                  ))}

                  {hiddenCount > 0 && !showAllEnrolled && (
                    <Typography
                      align="center"
                      sx={{
                        cursor: "pointer",
                        fontWeight: 600,
                        color: "#5aa6d6",
                      }}
                      onClick={() => setShowAllEnrolled(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        (e.key === "Enter" || e.key === " ") &&
                        setShowAllEnrolled(true)
                      }
                    >
                      + {hiddenCount} more
                    </Typography>
                  )}

                  {showAllEnrolled &&
                    enrolledEmails.length > MAX_VISIBLE_ENROLLED && (
                      <Typography
                        align="center"
                        sx={{
                          cursor: "pointer",
                          fontWeight: 600,
                          color: "#5aa6d6",
                        }}
                        onClick={() => setShowAllEnrolled(false)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          (e.key === "Enter" || e.key === " ") &&
                          setShowAllEnrolled(false)
                        }
                      >
                        - hide
                      </Typography>
                    )}
                </Stack>
              </Stack>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{ borderLeft: { md: "1px solid #e0e0e0" }, pl: { md: 4 } }}
            >
              <Stack spacing={3} alignItems="stretch">
                <Stack alignItems="center" spacing={1}>
                  <PeopleOutlineIcon sx={{ fontSize: 46, color: "#0a3759" }} />
                  <Typography
                    variant="h6"
                    align="center"
                    sx={{ fontWeight: 600, fontSize: "1.17em", maxWidth: 520 }}
                  >
                    {t.additionalUsersTitle}
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={contactByEmailCC}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setContactByEmailCC(checked);
                          if (checked) {
                            setEmailErrors(() =>
                              emails.map((val) => !!val && !isValidEmail(val))
                            );
                          } else {
                            setEmailErrors((errs) => errs.map(() => false));
                          }
                        }}
                        size="small"
                      />
                    }
                    label={<Typography>{t.emailCheckbox}</Typography>}
                  />

                  <Stack spacing={1} sx={{ pl: 4 }}>
                    {emails.map((email, idx) => {
                      const showError =
                        contactByEmailCC && !!email && !isValidEmail(email);
                      return (
                        <Stack key={`email-${idx}`} direction="row" spacing={1}>
                          <TextField
                            value={email}
                            onChange={(e) => setEmailAt(idx, e.target.value)}
                            size="small"
                            fullWidth
                            required={contactByEmailCC}
                            error={showError}
                            helperText={showError ? t.invalidEmail : " "}
                            variant="standard"
                          />
                          <Tooltip
                            title={
                              emailRemoveDisabled
                                ? "At least one email row is required"
                                : "Remove email"
                            }
                          >
                            <span>
                              <IconButton
                                aria-label="Remove email"
                                onClick={() => removeEmailAt(idx)}
                                edge="end"
                                disabled={emailRemoveDisabled}
                              >
                                <DeleteOutlineIcon
                                  sx={{
                                    color: "#62b5e5", // custom red
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      );
                    })}

                    <Button
                      onClick={addEmail}
                      startIcon={<AddIcon />}
                      variant="text"
                      sx={{ alignSelf: "flex-start", pl: 0, minWidth: 0 }}
                    >
                      {t.addNewEmail}
                    </Button>
                  </Stack>
                </Stack>

                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={contactByTextCC}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setContactByTextCC(checked);
                          if (checked) {
                            setPhoneErrors(() =>
                              phones.map((val) => !!val && !isValidPhone(val))
                            );
                          } else {
                            setPhoneErrors((errs) => errs.map(() => false));
                          }
                        }}
                        size="small"
                      />
                    }
                    label={<Typography>{t.textCheckbox}</Typography>}
                  />

                  <Stack spacing={1} sx={{ pl: 4 }}>
                    {phones.map((phone, idx) => {
                      const showError =
                        contactByTextCC && !!phone && !isValidPhone(phone);
                      return (
                        <Stack key={`phone-${idx}`} direction="row" spacing={1}>
                          <TextField
                            value={phone}
                            onChange={(e) => setPhoneAt(idx, e.target.value)}
                            size="small"
                            fullWidth
                            required={contactByTextCC}
                            error={showError}
                            helperText={showError ? t.invalidPhone : " "}
                            variant="outlined"
                            inputProps={{
                              inputMode: "numeric",
                              maxLength: 12,
                              pattern: "\\d{3}-\\d{3}-\\d{4}",
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                                { borderColor: "#e53935" },
                            }}
                          />
                          <Tooltip
                            title={
                              phoneRemoveDisabled
                                ? "At least one phone row is required"
                                : "Remove phone"
                            }
                          >
                            <span>
                              <IconButton
                                aria-label="Remove phone"
                                onClick={() => removePhoneAt(idx)}
                                edge="end"
                                disabled={phoneRemoveDisabled}
                              >
                                <DeleteOutlineIcon
                                  sx={{
                                    color: "#62b5e5", // custom red
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      );
                    })}

                    <Button
                      onClick={addPhone}
                      startIcon={<AddIcon />}
                      variant="text"
                      sx={{ alignSelf: "flex-start", pl: 0, minWidth: 0 }}
                    >
                      {t.addNewPhone}
                    </Button>
                  </Stack>
                </Stack>

                {formError && (
                  <Typography
                    color="error"
                    sx={{ textAlign: "center", mt: 1, fontWeight: 500 }}
                  >
                    {formError}
                  </Typography>
                )}

                <Box textAlign={"center"} className="buttons">
                  <Button
                    className="whiteButtonOutline"
                    sx={{
                      fontSize: "17px",
                      height: "50px",
                      padding: "0px 35px !important",
                    }}
                    onClick={handleSubmit}
                  >
                    {t.submit}
                  </Button>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <div style={{ textAlign: "center", marginTop: 40 }}>
        <Button
          className="blueButtonOutline"
          size="large"
          onClick={onContinueShopping}
          disabled={!onContinueShopping}
        >
          CONTINUE SHOPPING
        </Button>
      </div>
    </>
  );
};

export default DeliveryNotifications;
